"use client"

import styled from "@emotion/styled"
import Image from "next/image"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"
import { extractProperty, getUmbracoMediaUrl, replaceRichTextLinks } from "utils"
import WhiteBgHeading from "../../Projects/Marketing/ApartmentMarketingForRent/WhiteBgHeading"

const StyledSection = styled.section`
  padding-top: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  .strong-block span {
    display: block;
    font-weight: 600;
  }
  .second-heading {
    line-height: 120%;
    font-family: var(--narkis) !important;
  }
`

const StyledBGWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.advantagesBg4rentMarketing};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  padding: 60px 16px;
  gap: 24px;
  .swiper {
    padding-block: 0;
  }
  .swiper,
  .swiper-wrapper {
    overflow: visible;
  }
  .swiper-wrapper {
    align-items: stretch;
  }
  .swiper-slide {
    width: 322px;
    display: flex;
    overflow: hidden;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.bulletsBackgroundColor4rentMarketing};
    ${({ theme }) => theme.breakpoints.down(375)} {
      width: 300px;
    }
  }
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 88px 120px;
    gap: 64px;
  }
`
const StyledSwiperSlide = styled(SwiperSlide)`
  min-height: 415px;
  height: auto;
  ${({ theme }) => theme.breakpoints.up("md")} {
    min-height: 456px;
  }
`
const StyledItem = styled.article`
  height: 100%;
  width: 100%;
  background-color: white;
  padding: 16px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${({ theme }) => theme.colors.newBlack};
`
const StyledItemImage = styled.div`
  position: relative;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.blue4rentMarketing};
  overflow: hidden;
  width: 84px;
  height: 84px;
  margin-bottom: 16px;
`

const StyledItemRecommendName = styled.h3`
  margin: 0;
  padding: 0;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 130%;
  margin-bottom: 8px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    font-size: 24px;
    line-height: 150%;
    letter-spacing: -0.48px;
  }
`
const StyledItemPosition = styled.p`
  margin: 0;
  padding: 0;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 150%;
  padding: 8px;
  border-radius: 28px;
  background: #e4e4e4;
  margin-bottom: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    font-size: 16px;
  }
`
const StyledItemContent = styled.div`
  min-height: 138px;
  font-size: 16px;
  font-style: normal;
  font-weight: 300;
  line-height: 145%;
  & > * {
    &:first-child,
    &:last-child {
      margin-block: 0;
    }
  }
  ${({ theme }) => theme.breakpoints.up("md")} {
    min-height: 162px;
    font-size: 18px;
    line-height: 150%; /* 27px */
    letter-spacing: -0.36px;
  }
`

type Props = {
  data: any
}

const CareerEmployeeTestimonials = ({ data }: Props) => {
  const introDarkTitle = extractProperty(data, "introDarkTitle")
  const introLightTitle = extractProperty(data, "introLightTitle")
  const title = extractProperty(data, "title")
  const subTitle = extractProperty(data, "subtitle")
  const itemsList = (extractProperty(data, "jobsQuoteGalleryItems") || [])
    ?.map((item: any) => ({
      image: extractProperty(item, "image"),
      recommendName: extractProperty(item, "recommendName"),
      position: extractProperty(item, "position"),
      content: extractProperty(item, "content"),
    }))
    .filter((i: any) => Boolean(i))

  return (
    <StyledSection id="career-testimonials">
      <WhiteBgHeading
        desktopMarginBottom={60}
        mobileMarginBottom={60}
        titleCls="strong-block"
        title={`${introDarkTitle}<span><strong>${introLightTitle}</strong></span>`}
      />
      <StyledBGWrapper>
        <WhiteBgHeading
          title={title}
          subTitle={subTitle}
          subTitleFontWeight={300}
          titleCls="second-heading"
          isCentered
          desktopGap={4}
          mobileGap={4}
        />
        {/* Testimonials List Component can be placed here using itemsList data. it must be swiper */}
        <Swiper spaceBetween={24} slidesPerView={"auto"}>
          {itemsList.map(({ image, recommendName, position, content }: any, index: number) => (
            <StyledSwiperSlide key={index}>
              <StyledItem>
                <StyledItemImage>
                  {image && (
                    <Image
                      width={84}
                      height={84}
                      src={getUmbracoMediaUrl(image)}
                      alt={recommendName}
                    />
                  )}
                </StyledItemImage>
                {recommendName && (
                  <StyledItemRecommendName>{recommendName}</StyledItemRecommendName>
                )}
                {position && <StyledItemPosition>{position}</StyledItemPosition>}
                {content && (
                  <StyledItemContent
                    dangerouslySetInnerHTML={{
                      __html: replaceRichTextLinks(content),
                    }}
                  />
                )}
              </StyledItem>
            </StyledSwiperSlide>
          ))}
        </Swiper>
      </StyledBGWrapper>
    </StyledSection>
  )
}
export default CareerEmployeeTestimonials
